import { useState, useMemo, useEffect } from "react";
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  eachMonthOfInterval,
  subMonths,
} from "date-fns";
import { useToast, useDisclosure } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiInbox,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiActivity,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { AddNewOperation } from "./components/AddNewOperation";
import { selectOperations } from "../../../store/selectors/OperationsSelector";
import { Operation, OperationType } from "../../../entity/Operation";
import { WSOperation } from "../../../services/remote/operation/WSOperation";
import { setOperations } from "../../../store/slices/OperationSlice";
import Dialog from "../../components/Dialog";
import { Badge } from "../../components/Badge";
import { COLORS } from "../../../utils/Colors";
import "./Operations.css";

const DATE_FORMAT = "yyyy-MM-dd";
const MONTH_FORMAT = "yyyy-MM";
const CURRENCY = "TND";

type FlowFilter = "all" | "credit" | "debit";

const CREDIT_TYPES: string[] = [OperationType.TRANSFER_IN, OperationType.SELL];
const isCredit = (type: string) => CREDIT_TYPES.includes(type);

// operation type -> translation key
const TYPE_KEYS: Record<string, string> = {
  [OperationType.TRANSFER_IN]: "transfer_in",
  [OperationType.SELL]: "sell",
  [OperationType.BUY]: "buy",
  [OperationType.TEAM_SALARY]: "team_salary",
  [OperationType.ADS]: "ads",
  [OperationType.OTHER_TRANSFER_OUT]: "other_transfer_out",
};

const DATE_PRESETS = [
  { key: "7d", days: 7, labelKey: "operation.last_7_days", fallback: "7 days" },
  {
    key: "30d",
    days: 30,
    labelKey: "operation.last_30_days",
    fallback: "30 days",
  },
  {
    key: "90d",
    days: 90,
    labelKey: "operation.last_90_days",
    fallback: "90 days",
  },
];

const parseLocalDate = (value: string, endOfDay = false) =>
  new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00"}`);

const formatMoney = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const Operations = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const instance = WSOperation.getInstance();
  const operations = useSelector(selectOperations);

  const todayStr = format(new Date(), DATE_FORMAT);

  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), DATE_FORMAT),
    endDate: todayStr,
    month: "",
  });
  const [typeFilter, setTypeFilter] = useState<OperationType | "ALL">("ALL");
  const [flowFilter, setFlowFilter] = useState<FlowFilter>("all");
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState<Operation | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  /* ---------- derived data ---------- */

  const months = useMemo(
    () =>
      eachMonthOfInterval({
        start: subMonths(new Date(), 12),
        end: new Date(),
      }).reverse(),
    [],
  );

  const activePreset = DATE_PRESETS.find(
    (p) =>
      !dateRange.month &&
      dateRange.endDate === todayStr &&
      dateRange.startDate === format(subDays(new Date(), p.days), DATE_FORMAT),
  )?.key;

  // Operations inside the date range (used for the credit/debit chip counts)
  const rangeOperations = useMemo(() => {
    const start = dateRange.startDate
      ? parseLocalDate(dateRange.startDate).getTime()
      : 0;
    const end = dateRange.endDate
      ? parseLocalDate(dateRange.endDate, true).getTime()
      : Infinity;

    return (operations ?? []).filter((op) => {
      const time = new Date(op.date).getTime();
      return time >= start && time <= end;
    });
  }, [operations, dateRange]);

  const filteredOperations = useMemo(() => {
    return rangeOperations
      .filter((op) => {
        const matchesType = typeFilter === "ALL" || op.type === typeFilter;
        const matchesFlow =
          flowFilter === "all" ||
          (flowFilter === "credit" ? isCredit(op.type) : !isCredit(op.type));
        return matchesType && matchesFlow;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [rangeOperations, typeFilter, flowFilter]);

  const flowCounts = useMemo(() => {
    const credit = rangeOperations.filter((op) => isCredit(op.type)).length;
    return {
      all: rangeOperations.length,
      credit,
      debit: rangeOperations.length - credit,
    };
  }, [rangeOperations]);

  const totals = useMemo(() => {
    let credited = 0;
    let debited = 0;
    filteredOperations.forEach((op) => {
      if (isCredit(op.type)) credited += op.amount;
      else debited += op.amount;
    });
    return { credited, debited, net: credited - debited };
  }, [filteredOperations]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOperations.length / itemsPerPage),
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOperations = filteredOperations.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => {
    const visible = Math.min(5, totalPages);
    let start = Math.max(1, currentPage - 2);
    if (start + visible - 1 > totalPages) start = totalPages - visible + 1;
    return Array.from({ length: visible }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const hasActiveFilters = typeFilter !== "ALL" || flowFilter !== "all";

  /* ---------- handlers ---------- */

  const showToast = (
    title: string,
    status: "success" | "error",
    description?: string,
  ) =>
    toast({
      title,
      description,
      status,
      duration: status === "error" ? 5000 : 3000,
      isClosable: true,
    });

  const paginate = (page: number) =>
    setCurrentPage(Math.min(Math.max(1, page), totalPages));

  const applyPreset = (days: number) => {
    setDateRange({
      startDate: format(subDays(new Date(), days), DATE_FORMAT),
      endDate: todayStr,
      month: "",
    });
    setCurrentPage(1);
  };

  const handleMonthChange = (month: string) => {
    if (month) {
      const base = new Date(`${month}-01T00:00:00`);
      setDateRange({
        month,
        startDate: format(startOfMonth(base), DATE_FORMAT),
        endDate: format(endOfMonth(base), DATE_FORMAT),
      });
    } else {
      setDateRange({
        month: "",
        startDate: format(subDays(new Date(), 30), DATE_FORMAT),
        endDate: todayStr,
      });
    }
    setCurrentPage(1);
  };

  const handleFlowChange = (flow: FlowFilter) => {
    setFlowFilter(flow);
    setTypeFilter("ALL");
    setCurrentPage(1);
  };

  const handleTypeChange = (type: OperationType | "ALL") => {
    setTypeFilter(type);
    setFlowFilter("all");
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setTypeFilter("ALL");
    setFlowFilter("all");
    setCurrentPage(1);
  };

  const handleAddClick = () => {
    setSelectedOperation(null);
    onOpen();
  };

  const handleEditOperation = (operation: Operation) => {
    setSelectedOperation(operation);
    onOpen();
  };

  const handleDeleteClick = (operation: Operation) => {
    setOperationToDelete(operation);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!operationToDelete) return;
    try {
      await instance.deleteOperation(operationToDelete.id);
      dispatch(
        setOperations(
          operations.filter((op) => op.id !== operationToDelete.id),
        ),
      );
      showToast(t("operation.deleted", "Operation deleted"), "success");
    } catch (error) {
      console.error("Failed to delete operation:", error);
      showToast(
        t("operation.deleteError", "Error deleting operation"),
        "error",
      );
    } finally {
      setIsDeleteDialogOpen(false);
      setOperationToDelete(null);
    }
  };

  const handleCreateOrUpdateOperation = async (operation: Operation) => {
    try {
      if (selectedOperation) {
        await instance.updateOperation(operation);
        dispatch(
          setOperations(
            operations.map((op) => (op.id === operation.id ? operation : op)),
          ),
        );
        showToast(t("operation.updated", "Operation updated"), "success");
      } else {
        const response = await instance.addNewOperation(operation);
        dispatch(
          setOperations([{ ...operation, id: response }, ...operations]),
        );
        showToast(t("operation.created", "Operation created"), "success");
      }
      setSelectedOperation(null);
      onClose();
    } catch (error) {
      console.error("Operation error:", error);
      showToast(
        selectedOperation
          ? t("operation.updateError", "Error updating operation")
          : t("operation.createError", "Error creating operation"),
        "error",
        error instanceof Error ? error.message : "An error occurred",
      );
    }
  };

  /* ---------- render helpers ---------- */

  const renderTypeBadge = (type: string) => {
    const credit = isCredit(type);
    const key = TYPE_KEYS[type];
    const text = key
      ? `${t(credit ? "operation.credit" : "operation.debit")} - ${t(`operation.${key}`)}`
      : type.replace(/_/g, " ");

    return (
      <Badge
        text={text}
        color={credit ? COLORS.Palette.LightGreen : COLORS.Palette.LightRed}
        textColor={
          credit ? COLORS.Palette.MediumGreen : COLORS.Palette.MediumRed
        }
      />
    );
  };

  const flowChips: { key: FlowFilter; label: string }[] = [
    { key: "all", label: t("operation.allTypes") },
    { key: "credit", label: t("operation.credit") },
    { key: "debit", label: t("operation.debit") },
  ];

  /* ---------- render ---------- */

  return (
    <div className="operation-container">
      {/* Header */}
      <div className="operation-header">
        <div className="operation-heading">
          <h2 className="operation-title">
            {t("sidebar.operations", "Operations")}
          </h2>
          <span className="operation-count">{operations?.length ?? 0}</span>
        </div>
        <button className="operation-add-button" onClick={handleAddClick}>
          <FiPlus size={16} />
          {t("operation.addOperation")}
        </button>
      </div>

      {/* Stats */}
      <div className="operation-stats">
        <div className="operation-stat-card">
          <div className="operation-stat-icon operation-stat-icon-success">
            <FiArrowDownLeft size={18} />
          </div>
          <div className="operation-stat-body">
            <span className="operation-stat-label">
              {t("operation.totalCredited")}
            </span>
            <span className="operation-stat-value operation-positive">
              {formatMoney(totals.credited)} <small>{CURRENCY}</small>
            </span>
          </div>
        </div>

        <div className="operation-stat-card">
          <div className="operation-stat-icon operation-stat-icon-danger">
            <FiArrowUpRight size={18} />
          </div>
          <div className="operation-stat-body">
            <span className="operation-stat-label">
              {t("operation.totalDebited")}
            </span>
            <span className="operation-stat-value operation-negative">
              {formatMoney(totals.debited)} <small>{CURRENCY}</small>
            </span>
          </div>
        </div>

        <div className="operation-stat-card">
          <div className="operation-stat-icon operation-stat-icon-brand"></div>
          <div className="operation-stat-body">
            <span className="operation-stat-label">
              {t("operation.netBalance")}
            </span>
            <span
              className={`operation-stat-value ${
                totals.net >= 0 ? "operation-positive" : "operation-negative"
              }`}
            >
              {totals.net > 0 ? "+" : ""}
              {formatMoney(totals.net)} <small>{CURRENCY}</small>
            </span>
          </div>
        </div>

        <div className="operation-stat-card">
          <div className="operation-stat-icon operation-stat-icon-neutral">
            <FiActivity size={18} />
          </div>
          <div className="operation-stat-body">
            <span className="operation-stat-label">
              {t("operation.transactions", "Transactions")}
            </span>
            <span className="operation-stat-value">
              {filteredOperations.length}
            </span>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div className="operation-table-card">
        {/* Filters */}
        <div className="operation-toolbar">
          <div className="operation-field">
            <label className="operation-field-label">
              {t("operation.fromDate")}
            </label>
            <input
              type="date"
              className="operation-input"
              value={dateRange.startDate}
              max={dateRange.endDate}
              onChange={(e) => {
                setDateRange((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                  month: "",
                }));
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="operation-field">
            <label className="operation-field-label">
              {t("operation.toDate")}
            </label>
            <input
              type="date"
              className="operation-input"
              value={dateRange.endDate}
              min={dateRange.startDate}
              max={todayStr}
              onChange={(e) => {
                setDateRange((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                  month: "",
                }));
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="operation-field">
            <label className="operation-field-label">
              {t("operation.orSelectMonth")}
            </label>
            <select
              className="operation-input operation-select"
              value={dateRange.month}
              onChange={(e) => handleMonthChange(e.target.value)}
            >
              <option value="">{t("operation.allMonths")}</option>
              {months.map((month) => (
                <option
                  key={month.toISOString()}
                  value={format(month, MONTH_FORMAT)}
                >
                  {format(month, "MMMM yyyy")}
                </option>
              ))}
            </select>
          </div>

          <div className="operation-field">
            <label className="operation-field-label">
              {t("operation.filterByType")}
            </label>
            <select
              className="operation-input operation-select"
              value={typeFilter}
              onChange={(e) =>
                handleTypeChange(e.target.value as OperationType | "ALL")
              }
            >
              <option value="ALL">{t("operation.allTypes")}</option>
              {Object.values(OperationType).map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          <div className="operation-presets">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                className={`operation-preset ${
                  activePreset === preset.key ? "operation-preset-active" : ""
                }`}
                onClick={() => applyPreset(preset.days)}
              >
                {t(preset.labelKey, preset.fallback)}
              </button>
            ))}
          </div>
        </div>

        {/* Credit / debit chips */}
        <div className="operation-chips" role="tablist">
          {flowChips.map((chip) => (
            <button
              key={chip.key}
              role="tab"
              aria-selected={flowFilter === chip.key}
              className={`operation-chip operation-chip-${chip.key} ${
                flowFilter === chip.key ? "operation-chip-active" : ""
              }`}
              onClick={() => handleFlowChange(chip.key)}
            >
              {chip.label}
              <span className="operation-chip-count">
                {flowCounts[chip.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        {filteredOperations.length === 0 ? (
          <div className="operation-empty">
            <div className="operation-empty-icon">
              <FiInbox size={28} />
            </div>
            <h3>{t("operation.noOperations", "No operations found")}</h3>
            <p>
              {hasActiveFilters
                ? t("operation.tryAdjusting", "Try adjusting your filters.")
                : t(
                    "operation.emptyRange",
                    "No operations in the selected date range.",
                  )}
            </p>
            {hasActiveFilters ? (
              <button
                className="operation-secondary-button"
                onClick={resetFilters}
              >
                {t("operation.resetFilters", "Reset filters")}
              </button>
            ) : (
              <button className="operation-add-button" onClick={handleAddClick}>
                <FiPlus size={16} />
                {t("operation.addOperation")}
              </button>
            )}
          </div>
        ) : (
          <div className="operation-table-wrap">
            <table className="operation-table">
              <thead>
                <tr>
                  <th>{t("operation.date", "Date")}</th>
                  <th>{t("operation.amount")}</th>
                  <th>{t("operation.type")}</th>
                  <th>{t("operation.description")}</th>
                  <th>{t("operation.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {currentOperations.map((op) => {
                  const credit = isCredit(op.type);
                  return (
                    <tr key={op.id} className="operation-row">
                      <td
                        className="operation-date-cell"
                        data-label={t("operation.date", "Date")}
                      >
                        <div className="operation-date">
                          <span className="operation-date-day">
                            {format(new Date(op.date), "PP")}
                          </span>
                          <span className="operation-date-time">
                            {format(new Date(op.date), "p")}
                          </span>
                        </div>
                      </td>

                      <td data-label={t("operation.amount")}>
                        <span
                          className={`operation-amount ${
                            credit ? "operation-positive" : "operation-negative"
                          }`}
                        >
                          {credit ? "+" : "−"}
                          {formatMoney(op.amount)} <small>{CURRENCY}</small>
                        </span>
                      </td>

                      <td data-label={t("operation.type")}>
                        {renderTypeBadge(op.type)}
                      </td>

                      <td
                        className="operation-description-cell"
                        data-label={t("operation.description")}
                      >
                        <span
                          className="operation-description"
                          title={op.description}
                        >
                          {op.description || "—"}
                        </span>
                      </td>

                      <td className="operation-actions" data-label="">
                        <button
                          className="operation-action-button"
                          aria-label="Edit operation"
                          title="Edit"
                          onClick={() => handleEditOperation(op)}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="operation-action-button operation-action-danger"
                          aria-label="Delete operation"
                          title="Delete"
                          onClick={() => handleDeleteClick(op)}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {filteredOperations.length > 0 && (
          <div className="operation-footer">
            <div className="operation-footer-info operation-hide-mobile">
              {t("operation.showing", "Showing")} {indexOfFirstItem + 1}–
              {Math.min(indexOfLastItem, filteredOperations.length)}{" "}
              {t("operation.of", "of")} {filteredOperations.length}
            </div>

            <div className="operation-footer-controls">
              <div className="operation-per-page">
                <span className="operation-hide-mobile">
                  {t("operation.itemsPerPage", "Items per page")}:
                </span>
                <select
                  className="operation-input operation-select operation-select-sm"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="operation-pagination">
                <button
                  className="operation-page-button"
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  <FiChevronsLeft size={15} />
                </button>
                <button
                  className="operation-page-button"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <FiChevronLeft size={15} />
                </button>
                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    className={`operation-page-button ${
                      currentPage === page ? "operation-page-active" : ""
                    }`}
                    onClick={() => paginate(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="operation-page-button"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <FiChevronRight size={15} />
                </button>
                <button
                  className="operation-page-button"
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                >
                  <FiChevronsRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AddNewOperation
        isOpen={isOpen}
        onClose={() => {
          setSelectedOperation(null);
          onClose();
        }}
        operation={selectedOperation!!}
        onCreate={handleCreateOrUpdateOperation}
      />

      <Dialog
        open={isDeleteDialogOpen}
        title={t("operation.deleteTitle", "Delete Operation")}
        description={t(
          "operation.deleteDescription",
          "Are you sure you want to delete this operation? This action cannot be undone.",
        )}
        positiveButtonText={t("operation.delete", "Delete")}
        onClick={handleDeleteConfirm}
        negativeButtonText={t("operation.cancel", "Cancel")}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
    </div>
  );
};
